import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Trash2, AlertTriangle, DollarSign } from "lucide-react";

interface PurgeRecord {
  id: string;
  seller_id: string;
  first_name: string | null;
  last_name: string | null;
  business_name: string | null;
  county: string | null;
  origin_state: string | null;
  consideration: number | null;
  recorded_date: string | null;
  times_mailed: number;
  mailing_status: string;
  response: string | null;
}

export function ConsiderationPurgeReview({ onComplete }: { onComplete?: () => void }) {
  const [records, setRecords] = useState<PurgeRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      // Two tiers: <$60k if recorded 5+ years ago, <$50k otherwise
      const fiveYearsAgo = new Date();
      fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);
      const fiveYearCutoff = fiveYearsAgo.toISOString().split("T")[0];

      const [older, newer] = await Promise.all([
        supabase
          .from("mailing_records")
          .select("id, seller_id, first_name, last_name, business_name, county, origin_state, consideration, recorded_date, times_mailed, mailing_status, response")
          .not("consideration", "is", null)
          .gt("consideration", 0)
          .lt("consideration", 60000)
          .lte("recorded_date", fiveYearCutoff)
          .order("consideration", { ascending: true }),
        supabase
          .from("mailing_records")
          .select("id, seller_id, first_name, last_name, business_name, county, origin_state, consideration, recorded_date, times_mailed, mailing_status, response")
          .not("consideration", "is", null)
          .gt("consideration", 0)
          .lt("consideration", 50000)
          .gt("recorded_date", fiveYearCutoff)
          .order("consideration", { ascending: true }),
      ]);

      if (older.error) throw older.error;
      if (newer.error) throw newer.error;

      // Merge and deduplicate by id
      const merged = new Map<string, any>();
      for (const r of [...(older.data || []), ...(newer.data || [])]) merged.set(r.id, r);
      const data = Array.from(merged.values()).sort((a, b) => (a.consideration || 0) - (b.consideration || 0));

      setRecords(data);
    } catch (e: any) {
      toast.error("Failed to load: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleExclude = (id: string) => {
    setExcluded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const recordsToPurge = records.filter(r => !excluded.has(r.id));

  const handlePurge = async () => {
    setDeleting(true);
    try {
      const ids = recordsToPurge.map(r => r.id);
      // Delete in batches of 100
      for (let i = 0; i < ids.length; i += 100) {
        const batch = ids.slice(i, i + 100);
        // First delete mailing_history for these records
        const { error: histErr } = await supabase
          .from("mailing_history")
          .delete()
          .in("mailing_record_id", batch);
        if (histErr) throw histErr;

        const { error } = await supabase
          .from("mailing_records")
          .delete()
          .in("id", batch);
        if (error) throw error;
      }
      toast.success(`Purged ${ids.length} records`);
      setConfirmOpen(false);
      onComplete?.();
      fetchRecords();
    } catch (e: any) {
      toast.error("Delete failed: " + e.message);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="h-5 w-5" />
          Consideration Purge Review
        </CardTitle>
        <CardDescription>
          Records with consideration &lt; $50,000 recorded on or before 01/01/2022.
          Click a row to exclude it from deletion. {excluded.size > 0 && (
            <Badge variant="secondary" className="ml-2">{excluded.size} excluded</Badge>
          )}
        </CardDescription>
        <div className="flex items-center gap-3 pt-2">
          <Badge variant="outline" className="text-sm">
            {records.length} total matches
          </Badge>
          <Badge variant="destructive" className="text-sm">
            {recordsToPurge.length} to purge
          </Badge>
          {recordsToPurge.length > 0 && (
            <Button variant="destructive" size="sm" onClick={() => setConfirmOpen(true)}>
              <Trash2 className="h-4 w-4 mr-1" /> Purge {recordsToPurge.length} Records
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border overflow-auto" style={{ maxHeight: "calc(100vh - 380px)" }}>
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="text-xs w-[50px]">Keep</TableHead>
                <TableHead className="text-xs">Seller ID</TableHead>
                <TableHead className="text-xs">Name</TableHead>
                <TableHead className="text-xs">County</TableHead>
                <TableHead className="text-xs">State</TableHead>
                <TableHead className="text-xs text-right">Consideration</TableHead>
                <TableHead className="text-xs">Recorded</TableHead>
                <TableHead className="text-xs text-center">#Mailed</TableHead>
                <TableHead className="text-xs">Response</TableHead>
                <TableHead className="text-xs">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.map(r => {
                const isExcluded = excluded.has(r.id);
                return (
                  <TableRow
                    key={r.id}
                    className={`cursor-pointer ${isExcluded ? "bg-green-50 dark:bg-green-950/20" : "hover:bg-destructive/5"}`}
                    onClick={() => toggleExclude(r.id)}
                  >
                    <TableCell className="text-center">
                      {isExcluded ? (
                        <Badge variant="default" className="text-xs px-1">KEEP</Badge>
                      ) : (
                        <Badge variant="destructive" className="text-xs px-1 opacity-50">DEL</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">{r.seller_id}</TableCell>
                    <TableCell className="text-xs font-medium">
                      {r.business_name || `${r.first_name || ""} ${r.last_name || ""}`.trim() || "—"}
                    </TableCell>
                    <TableCell className="text-xs">{r.county || "—"}</TableCell>
                    <TableCell className="text-xs">{r.origin_state || "—"}</TableCell>
                    <TableCell className="text-xs text-right font-mono">
                      ${r.consideration?.toLocaleString() ?? "—"}
                    </TableCell>
                    <TableCell className="text-xs">{r.recorded_date || "—"}</TableCell>
                    <TableCell className="text-xs text-center">{r.times_mailed}</TableCell>
                    <TableCell className="text-xs">
                      {r.response ? <Badge variant="outline" className="text-xs">{r.response}</Badge> : "—"}
                    </TableCell>
                    <TableCell className="text-xs">{r.mailing_status}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" /> Confirm Purge
            </DialogTitle>
            <DialogDescription>
              This will permanently delete <strong>{recordsToPurge.length}</strong> mailing records
              (and their mailing history) with consideration under $50,000 recorded on or before 01/01/2022.
              {excluded.size > 0 && <> You've excluded {excluded.size} records that will be kept.</>}
              This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handlePurge} disabled={deleting}>
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />}
              Delete {recordsToPurge.length} Records
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
